import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Eye, EyeSlash, CheckCircle, XCircle, ArrowRight, ArrowLeft } from '@phosphor-icons/react';

/**
 * Self-service registratie met 3-staps wizard + verplichte VIES BTW-validatie (SaaS Faz 1+).
 *
 * Stappen:
 *  1) Account: e-mail + wachtwoord + naam
 *  2) Bedrijfsgegevens: bedrijfsnaam, BTW (verplicht), adres → VIES-check + auto-fill
 *  3) Bevestigen + start 14-daagse trial (ALLEEN na geldige VIES)
 */
export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [stap, setStap] = useState(1);

  // Stap 1 — account
  const [form, setForm] = useState({
    email: '', name: '', password: '', wachtwoord_check: '',
    bedrijfsnaam: '', btw_nr: '',
    adres_straat: '', adres_nr: '', adres_bus: '', adres_postcode: '', adres_stad: '', adres_land: 'België',
    telefoon: '', iban: '',
  });
  const [showPw, setShowPw] = useState(false);
  const [viesStatus, setViesStatus] = useState(null); // null | 'checking' | 'valid' | 'invalid' | 'pending'
  const [viesError, setViesError] = useState('');
  const [viesData, setViesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleViesCheck = async () => {
    setError('');
    setViesError('');
    setViesData(null);
    if (!form.btw_nr.trim()) {
      setViesStatus(null);
      return;
    }
    setViesStatus('checking');
    try {
      const r = await API.get('/public/vies-check', { params: { btwnr: form.btw_nr.trim() } });
      const data = r.data;
      if (data.valid === true) {
        setViesStatus('valid');
        setViesData(data);
        // Auto-fill bedrijfsnaam + adres uit officiële database als velden leeg zijn
        setForm(p => {
          const adr = data.adres_geparseerd || {};
          return {
            ...p,
            bedrijfsnaam: p.bedrijfsnaam || data.company_name || '',
            adres_straat: p.adres_straat || adr.straat || '',
            adres_nr: p.adres_nr || adr.nr || '',
            adres_postcode: p.adres_postcode || adr.postcode || '',
            adres_stad: p.adres_stad || adr.stad || '',
          };
        });
      } else if (data.transient === true) {
        // ⚠️ VIES lidstaat-database tijdelijk down (bv. MS_UNAVAILABLE voor BE)
        // → laat doorgaan met 'pending' status; bedrijfsnaam moet manueel ingevuld worden
        setViesStatus('pending');
        setViesError('');
      } else {
        setViesStatus('invalid');
        setViesError(data.error || 'BTW-nummer niet geldig — controleer het nummer');
      }
    } catch (e) {
      setViesStatus('invalid');
      setViesError(e.response?.data?.detail || 'BTW-controle tijdelijk niet beschikbaar — probeer later opnieuw');
    }
  };

  const valideerStap1 = () => {
    if (!form.email.trim()) return 'E-mailadres is verplicht';
    if (!form.email.includes('@')) return 'Geldig e-mailadres invullen';
    if (!form.password) return 'Wachtwoord is verplicht';
    if (form.password.length < 8) return 'Wachtwoord min. 8 karakters';
    if (form.password !== form.wachtwoord_check) return 'Wachtwoorden komen niet overeen';
    return null;
  };
  const valideerStap2 = () => {
    if (!form.bedrijfsnaam.trim()) return 'Bedrijfsnaam is verplicht';
    if (!form.btw_nr.trim()) return 'BTW-nummer is verplicht';
    if (viesStatus === 'checking') return 'BTW-nummer wordt gecontroleerd, even wachten...';
    // 'valid' (VIES OK) of 'pending' (VIES tijdelijk down) → mag door
    if (viesStatus !== 'valid' && viesStatus !== 'pending') return 'BTW-nummer is niet geldig';
    if (!form.adres_straat.trim() || !form.adres_postcode.trim() || !form.adres_stad.trim()) return 'Volledig adres invullen';
    return null;
  };

  const submit = async () => {
    setError('');
    const v1 = valideerStap1();
    if (v1) { setError(v1); setStap(1); return; }
    const v2 = valideerStap2();
    if (v2) { setError(v2); setStap(2); return; }
    setLoading(true);
    const result = await register(form.email.trim().toLowerCase(), form.password,
                                   form.name.trim() || form.bedrijfsnaam.trim(), form.bedrijfsnaam.trim(),
                                   { ...form, vies_valid: viesStatus === 'valid' });
    if (result.ok) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Registratie mislukt. Probeer opnieuw.');
    }
    setLoading(false);
  };

  const ProgressBar = () => (
    <div className="flex items-center justify-between mb-6 max-w-md mx-auto">
      {[1, 2, 3].map(i => (
        <div key={i} className="flex items-center flex-1">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-xs transition-colors ${
              stap === i ? 'bg-[#002FA7] text-white shadow-lg' :
              stap > i ? 'bg-green-500 text-white' :
              'bg-gray-200 text-gray-500'
            }`}
            data-testid={`step-indicator-${i}`}
          >
            {stap > i ? '✓' : i}
          </div>
          {i < 3 && <div className={`flex-1 h-0.5 mx-1 ${stap > i ? 'bg-green-500' : 'bg-gray-200'}`}></div>}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            <span className="text-[#002FA7]">Bill</span>Fac
          </h1>
          <p className="text-gray-500 mt-2 text-sm">
            {stap === 1 && 'Stap 1 van 3 — Maak je account'}
            {stap === 2 && 'Stap 2 van 3 — Bedrijfsgegevens'}
            {stap === 3 && 'Stap 3 van 3 — Bevestig & start trial'}
          </p>
        </div>

        <ProgressBar />

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* ───── Stap 1: Account ───── */}
          {stap === 1 && (
            <div data-testid="stap-1">
              <div className="space-y-3">
                <div>
                  <label className="form-label">E-mailadres <span className="text-red-500">*</span></label>
                  <input type="email" className="field-input" value={form.email} onChange={u('email')} placeholder="jij@bedrijf.be" data-testid="register-email" autoComplete="email" autoFocus />
                </div>
                <div>
                  <label className="form-label">Voornaam + Achternaam</label>
                  <input type="text" className="field-input" value={form.name} onChange={u('name')} placeholder="Jan Janssens" data-testid="register-name" autoComplete="name" />
                </div>
                <div>
                  <label className="form-label">Wachtwoord <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input type={showPw ? 'text' : 'password'} className="field-input pr-10" value={form.password} onChange={u('password')} placeholder="Min. 8 karakters" data-testid="register-password" autoComplete="new-password" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setShowPw(!showPw)}>
                      {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="form-label">Herhaal wachtwoord <span className="text-red-500">*</span></label>
                  <input type={showPw ? 'text' : 'password'} className="field-input" value={form.wachtwoord_check} onChange={u('wachtwoord_check')} placeholder="Herhaal wachtwoord" data-testid="register-password-check" autoComplete="new-password" />
                </div>
              </div>
              {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm" data-testid="register-error">{error}</div>}
              <button onClick={() => { const v = valideerStap1(); if (v) setError(v); else { setError(''); setStap(2); } }} className="btn btn-primary w-full mt-6 justify-center" data-testid="stap-1-next">
                Volgende stap <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* ───── Stap 2: Bedrijfsgegevens (auto-check op achtergrond) ───── */}
          {stap === 2 && (
            <div data-testid="stap-2">
              <div className="space-y-3">
                {/* BTW eerst — auto-vult bedrijfsgegevens op achtergrond */}
                <div>
                  <label className="form-label">BTW-nummer <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="text"
                      className="field-input pr-10"
                      value={form.btw_nr}
                      onChange={(e) => { u('btw_nr')(e); setViesStatus(null); setViesData(null); setViesError(''); }}
                      onBlur={() => { if (form.btw_nr.trim().length >= 4 && viesStatus !== 'valid') handleViesCheck(); }}
                      placeholder="BE0123456789"
                      data-testid="register-btwnr"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {viesStatus === 'checking' && <span className="text-gray-400 text-xs">⏳</span>}
                      {viesStatus === 'valid' && <CheckCircle size={16} className="text-green-600" />}
                      {viesStatus === 'pending' && <span className="text-amber-500 text-sm">⚠️</span>}
                      {viesStatus === 'invalid' && <XCircle size={16} className="text-red-500" />}
                    </div>
                  </div>
                  {viesStatus === 'valid' && (
                    <div className="mt-1.5 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700 flex items-start gap-1.5" data-testid="vies-success">
                      <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>BTW-nummer bevestigd</strong>
                        {viesData?.company_name && <div className="text-[11px] mt-0.5 opacity-80">{viesData.company_name}</div>}
                      </div>
                    </div>
                  )}
                  {viesStatus === 'pending' && (
                    <div className="mt-1.5 p-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800 flex items-start gap-1.5" data-testid="vies-pending">
                      <span className="text-base leading-none mt-0.5">⚠️</span>
                      <div>
                        <strong>VIES tijdelijk onbereikbaar</strong>
                        <div className="text-[11px] mt-0.5 leading-relaxed">
                          De officiële BTW-database van de lidstaat is momenteel down (MS_UNAVAILABLE).
                          Je kunt nu doorgaan met je registratie — wij valideren je BTW automatisch zodra de service terug online is.
                          Vul wel zelf je bedrijfsnaam en adres in.
                        </div>
                      </div>
                    </div>
                  )}
                  {viesStatus === 'invalid' && (
                    <div className="mt-1.5 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-start gap-1.5" data-testid="vies-error">
                      <XCircle size={14} className="mt-0.5 flex-shrink-0" />
                      <div>{viesError}</div>
                    </div>
                  )}
                  <p className="text-[10px] text-gray-500 mt-1">⚖️ BTW-nummer wordt automatisch gecontroleerd. Vul bv. BE0123456789.</p>
                </div>

                <div>
                  <label className="form-label">Bedrijfsnaam <span className="text-red-500">*</span></label>
                  <input type="text" className="field-input" value={form.bedrijfsnaam} onChange={u('bedrijfsnaam')} placeholder="bv. Jansen BV" data-testid="register-bedrijfsnaam" autoComplete="organization" />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className="form-label">Straat <span className="text-red-500">*</span></label>
                    <input type="text" className="field-input" value={form.adres_straat} onChange={u('adres_straat')} placeholder="Kerkstraat" data-testid="register-straat" autoComplete="street-address" />
                  </div>
                  <div>
                    <label className="form-label">Nr</label>
                    <input type="text" className="field-input" value={form.adres_nr} onChange={u('adres_nr')} placeholder="12" data-testid="register-nr" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="form-label">Postcode <span className="text-red-500">*</span></label>
                    <input type="text" className="field-input" value={form.adres_postcode} onChange={u('adres_postcode')} placeholder="2000" data-testid="register-postcode" autoComplete="postal-code" />
                  </div>
                  <div className="col-span-2">
                    <label className="form-label">Gemeente <span className="text-red-500">*</span></label>
                    <input type="text" className="field-input" value={form.adres_stad} onChange={u('adres_stad')} placeholder="Antwerpen" data-testid="register-stad" autoComplete="address-level2" />
                  </div>
                </div>
                <div>
                  <label className="form-label">Telefoon</label>
                  <input type="text" className="field-input" value={form.telefoon} onChange={u('telefoon')} placeholder="+32 (0)..." data-testid="register-telefoon" autoComplete="tel" />
                </div>
              </div>

              {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{error}</div>}

              <div className="flex gap-2 mt-6">
                <button onClick={() => { setError(''); setStap(1); }} className="btn btn-secondary flex-1 justify-center" data-testid="stap-2-prev">
                  <ArrowLeft size={14} /> Terug
                </button>
                <button onClick={() => { const v = valideerStap2(); if (v) setError(v); else { setError(''); setStap(3); } }} className="btn btn-primary flex-1 justify-center" data-testid="stap-2-next">
                  Volgende <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ───── Stap 3: Bevestigen & start ───── */}
          {stap === 3 && (
            <div data-testid="stap-3">
              <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
                <div className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-green-800">Klaar om te starten</div>
                    <div className="text-xs text-green-700 mt-1">Je BTW-nummer is gecontroleerd. Na bevestiging start je 14-daagse proefperiode.</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded p-4 text-xs space-y-1.5">
                <div><span className="text-gray-500">E-mail:</span> <strong>{form.email}</strong></div>
                <div><span className="text-gray-500">Naam:</span> <strong>{form.name || '—'}</strong></div>
                <div><span className="text-gray-500">Bedrijf:</span> <strong>{form.bedrijfsnaam}</strong></div>
                <div><span className="text-gray-500">BTW:</span> <strong className="font-mono">{form.btw_nr}</strong> <span className="text-green-600">✓ gecontroleerd</span></div>
                <div><span className="text-gray-500">Adres:</span> <strong>{form.adres_straat} {form.adres_nr}, {form.adres_postcode} {form.adres_stad}</strong></div>
                {form.telefoon && <div><span className="text-gray-500">Tel:</span> <strong>{form.telefoon}</strong></div>}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4 text-xs">
                <div className="font-bold text-blue-800 mb-1">🎁 Wat krijg je?</div>
                <ul className="space-y-0.5 text-blue-700">
                  <li>✓ 14 dagen volledige toegang</li>
                  <li>✓ Geen creditcard nodig</li>
                  <li>✓ Eigen geïsoleerde data</li>
                  <li>✓ Onbeperkte facturen, klanten, artikels</li>
                </ul>
              </div>

              {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{error}</div>}

              <div className="flex gap-2 mt-6">
                <button onClick={() => { setError(''); setStap(2); }} className="btn btn-secondary flex-1 justify-center" disabled={loading} data-testid="stap-3-prev">
                  <ArrowLeft size={14} /> Terug
                </button>
                <button onClick={submit} className="btn btn-primary flex-1 justify-center" disabled={loading} data-testid="register-submit">
                  {loading ? 'Bezig...' : '🚀 Start 14-daagse proef'}
                </button>
              </div>
            </div>
          )}

          <div className="text-center mt-4 pt-3 border-t border-gray-100 text-xs text-gray-600">
            Heb je al een account?{' '}
            <Link to="/login" className="text-[#002FA7] font-semibold hover:underline" data-testid="register-naar-login">
              Log in
            </Link>
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-gray-400">
          © {new Date().getFullYear()} BillFac. Alle rechten voorbehouden.
        </div>
      </div>
    </div>
  );
}
